import React from 'react';

import type { DocumentElement } from '@aline/common';
import {
  AlineTransforms,
  FooterType,
  HeaderType,
  minHeaderFooterContentHeightIn,
} from '@aline/common';
import { DialogActions, FormControlLabel, FormHelperText, Typography } from '@mui/material';
import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import { equals } from 'ramda';
import type { Point } from 'slate';
import { Editor } from 'slate';
import { AlineButton, AlineCheckbox, AlineDialog, AlineNumberInput } from 'src/components';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine/hooks';
import { useEditingDisabled } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

const useStyles = makeStyles()(theme => ({
  dialogPaper: {
    width: 360,
  },
  errMessage: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  input: {
    width: 72,
  },
  inputContainer: {
    alignItems: 'baseline',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  },
}));

interface FormValues {
  diffFirstPage: boolean;
  evenOdd: boolean;
  footerMargin: number;
  headerMargin: number;
}

export function HeaderFooterDialog(): JSX.Element | null {
  const { classes } = useStyles();
  const editor = useEditor();
  const cannotEdit = useEditingDisabled();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  const open = useEditorTabStateSelector(state => state.documentUi.headerFooterDialogOpen);
  const headerFooterProperties = useEditorTabStateSelector(
    state => state.derived.headerFooterProperties,
    equals
  );
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const {
    documentUi: { setHeaderFooterDialogOpen },
  } = useEditorTabStateActions();
  const headersEntry = Queries.getHeadersElement(editor);
  const footersEntry = Queries.getFootersElement(editor);

  function closeDialog(): void {
    setHeaderFooterDialogOpen(false);
  }

  function getSelectPointOnToggle(args: {
    diffFirstPage: boolean;
    evenOdd: boolean;
    prevActiveHeaderFooterProps: DocumentElement['headerFooter'];
  }): Point | void {
    const { diffFirstPage, evenOdd, prevActiveHeaderFooterProps } = args;
    const selectionBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor);

    if (!selectionBoundaryEntry || !headersEntry || !footersEntry) return;

    const [selectionBoundaryNode] = selectionBoundaryEntry;
    const toggleDiffFirstPageOn = !prevActiveHeaderFooterProps.diffFirstPage && diffFirstPage;
    const toggleEvenOddOn = !prevActiveHeaderFooterProps.evenOdd && evenOdd;
    const toggleDiffFirstPageOff = prevActiveHeaderFooterProps.diffFirstPage && !diffFirstPage;
    const toggleEvenOddOff = prevActiveHeaderFooterProps.evenOdd && !evenOdd;

    /**
     * Used to determine where to change the selection when headerFooterProps are toggled/changed.
     * For instance, if a user toggles evenOdd off and the selection is inside an even footer, we switch
     * the selection to the default footer as that is what will be rendered on toggle.
     */
    switch (selectionBoundaryNode.type) {
      case 'document':
        return;
      case HeaderType.Default: {
        const headerTypeToSelect = toggleDiffFirstPageOn
          ? HeaderType.FirstPage
          : toggleEvenOddOn
            ? HeaderType.Even
            : null;
        if (!headerTypeToSelect) return;

        const [, headerPathToSelect] = Queries.getHeaderOrFooter({
          editor,
          partEntry: headersEntry,
          type: headerTypeToSelect,
        });

        return Editor.start(editor, headerPathToSelect);
      }
      case HeaderType.FirstPage: {
        const headerTypeToSelect = toggleDiffFirstPageOff ? HeaderType.Default : null;
        if (!headerTypeToSelect) return;

        const [, headerPathToSelect] = Queries.getHeaderOrFooter({
          editor,
          partEntry: headersEntry,
          type: headerTypeToSelect,
        });

        return Editor.start(editor, headerPathToSelect);
      }
      case HeaderType.Even: {
        const headerTypeToSelect = toggleEvenOddOff ? HeaderType.Default : null;
        if (!headerTypeToSelect) return;

        const [, headerPathToSelect] = Queries.getHeaderOrFooter({
          editor,
          partEntry: headersEntry,
          type: headerTypeToSelect,
        });

        return Editor.start(editor, headerPathToSelect);
      }
      case FooterType.Default: {
        const footerTypeToSelect = toggleDiffFirstPageOn
          ? FooterType.FirstPage
          : toggleEvenOddOn
            ? FooterType.Even
            : null;
        if (!footerTypeToSelect) return;

        const [, footerPathToSelect] = Queries.getHeaderOrFooter({
          editor,
          partEntry: footersEntry,
          type: footerTypeToSelect,
        });

        return Editor.start(editor, footerPathToSelect);
      }
      case FooterType.FirstPage: {
        const footerTypeToSelect = toggleDiffFirstPageOff ? FooterType.Default : null;
        if (!footerTypeToSelect) return;

        const [, footerPathToSelect] = Queries.getHeaderOrFooter({
          editor,
          partEntry: footersEntry,
          type: footerTypeToSelect,
        });

        return Editor.start(editor, footerPathToSelect);
      }
      case FooterType.Even: {
        const footerTypeToSelect = toggleEvenOddOff ? FooterType.Default : null;
        if (!footerTypeToSelect) return;

        const [, footerPathToSelect] = Queries.getHeaderOrFooter({
          editor,
          partEntry: footersEntry,
          type: footerTypeToSelect,
        });

        return Editor.start(editor, footerPathToSelect);
      }
    }
  }

  if (!headersEntry || !footersEntry) return null;

  const [headersElement] = headersEntry;
  const [footersElement] = footersEntry;
  const activePageDimensions = Queries.activePageDimensions(editor, 'in');
  const maxHeaderFooterHeightIn = Queries.maxHeaderOrFooterHeight(activePageDimensions, 'in');
  const maxHeaderFooterMarginIn = maxHeaderFooterHeightIn - minHeaderFooterContentHeightIn;
  const HeaderFooterSchema = Yup.object().shape({
    diffFirstPage: Yup.boolean().required(),
    evenOdd: Yup.boolean().required(),
    footerMargin: Yup.number()
      .typeError('Footer margin must be a number')
      .min(0, 'Footer margin must be 0 or a positive number')
      .max(
        maxHeaderFooterMarginIn,
        `Footer margin can be be at most ${maxHeaderFooterMarginIn} inches`
      )
      .required('Required'),
    headerMargin: Yup.number()
      .typeError('Header margin must be a number')
      .min(0, 'Header margin must be 0 or a positive number')
      .max(
        maxHeaderFooterMarginIn,
        `Header margin can be be at most ${maxHeaderFooterMarginIn} inches`
      )
      .required('Required'),
  });

  return (
    <AlineDialog
      classes={{ paper: classes.dialogPaper }}
      open={open}
      title="Headers and footers"
      onClose={closeDialog}
    >
      <Typography variant="subtitle2">Margins</Typography>
      <Formik<FormValues>
        initialValues={{
          diffFirstPage: headerFooterProperties.diffFirstPage,
          evenOdd: headerFooterProperties.evenOdd,
          footerMargin: Queries.renderedHeaderOrFooterMargin(footersElement, 'in'),
          headerMargin: Queries.renderedHeaderOrFooterMargin(headersElement, 'in'),
        }}
        validationSchema={HeaderFooterSchema}
        onSubmit={({ diffFirstPage, evenOdd, footerMargin, headerMargin }): void => {
          AlineTransforms.withoutNormalizing(editor, () => {
            if (!currentUserId || !editingMode) return;

            // Make sure to capture the prev props before running any transforms that might change those values.
            const [document] = Queries.getDocument(editor);
            const prevActiveHeaderFooterProps = Queries.activeHeaderOrFooterProps(document);

            AuthorizedTransforms.setHeaderFooterProps(editor, {
              authorId: currentUserId,
              editingMode,
              newHeaderFooter: { diffFirstPage, evenOdd },
            });
            AuthorizedTransforms.setFootersElementProps(editor, {
              authorId: currentUserId,
              editingMode,
              props: {
                footerMargin,
              },
            });
            AuthorizedTransforms.setHeadersElementProps(editor, {
              authorId: currentUserId,
              editingMode,
              props: {
                headerMargin,
              },
            });

            const selectPoint = getSelectPointOnToggle({
              diffFirstPage,
              evenOdd,
              prevActiveHeaderFooterProps,
            });

            if (selectPoint) AlineTransforms.select(editor, selectPoint);

            ClientTransforms.focus(editor);

            closeDialog();
          });
        }}
      >
        {({
          dirty,
          errors,
          handleReset,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          touched,
        }): JSX.Element => {
          const headerMarginErr = !!touched.headerMargin && !!errors.headerMargin;
          const footerMarginErr = !!touched.footerMargin && !!errors.footerMargin;
          const headerMarginName = 'headerMargin';
          const footerMarginName = 'footerMargin';

          return (
            <form onReset={handleReset} onSubmit={handleSubmit}>
              <div className={classes.inputContainer}>
                <Typography noWrap color="textSecondary" variant="body1">
                  Header (inches from top)
                </Typography>
                <Field name={headerMarginName}>
                  {({ field, meta }: FieldProps<number, FormValues>): JSX.Element => {
                    const { error, touched } = meta;

                    // onChange causes coercion of value from number => string, resulting in bugs.
                    const { onChange, ...rest } = field;

                    return (
                      <AlineNumberInput
                        hideHelperText
                        className={classes.input}
                        disabled={cannotEdit}
                        error={touched && !!error}
                        fullWidth={false}
                        onValueChange={values => {
                          setFieldValue(headerMarginName, values.floatValue);
                        }}
                        {...rest}
                      />
                    );
                  }}
                </Field>
              </div>
              <div className={classes.errMessage}>
                <FormHelperText error={headerMarginErr}>
                  {headerMarginErr ? (errors.headerMargin ?? 'Header margin invalid') : ' '}
                </FormHelperText>
              </div>
              <div className={classes.inputContainer}>
                <Typography noWrap color="textSecondary" variant="body1">
                  Footer (inches from bottom)
                </Typography>
                <Field name={footerMarginName}>
                  {({ field, meta }: FieldProps<number, FormValues>): JSX.Element => {
                    const { error, touched } = meta;

                    // onChange causes coercion of value from number => string, resulting in bugs.
                    const { onChange, ...rest } = field;

                    return (
                      <AlineNumberInput
                        hideHelperText
                        className={classes.input}
                        disabled={cannotEdit}
                        error={touched && !!error}
                        fullWidth={false}
                        onValueChange={values => {
                          setFieldValue(footerMarginName, values.floatValue);
                        }}
                        {...rest}
                      />
                    );
                  }}
                </Field>
              </div>
              <div className={classes.errMessage}>
                <FormHelperText error={footerMarginErr}>
                  {footerMarginErr ? (errors.footerMargin ?? 'Footer margin invalid') : ' '}
                </FormHelperText>
              </div>
              <Typography gutterBottom variant="subtitle2">
                Layout
              </Typography>
              <Field name="diffFirstPage">
                {({ field }: FieldProps<boolean, FormValues>): JSX.Element => {
                  const { value } = field;

                  return (
                    <FormControlLabel
                      control={
                        <AlineCheckbox
                          disableHoverBackground
                          checked={value}
                          color="primary"
                          disabled={cannotEdit}
                          {...field}
                        />
                      }
                      label={<Typography variant="caption">Different first page</Typography>}
                    />
                  );
                }}
              </Field>
              <Field name="evenOdd">
                {({ field }: FieldProps<boolean, FormValues>): JSX.Element => {
                  const { value } = field;

                  return (
                    <FormControlLabel
                      control={
                        <AlineCheckbox
                          disableHoverBackground
                          checked={value}
                          color="primary"
                          disabled={cannotEdit}
                          {...field}
                        />
                      }
                      label={<Typography variant="caption">Different odd & even</Typography>}
                    />
                  );
                }}
              </Field>
              <DialogActions>
                <AlineButton color="primary" variant="outlined" onClick={closeDialog}>
                  Cancel
                </AlineButton>
                <AlineButton
                  color="primary"
                  disabled={!dirty || isSubmitting || cannotEdit}
                  type="submit"
                  variant="contained"
                >
                  Apply
                </AlineButton>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </AlineDialog>
  );
}
